import { useCallback, useEffect, useRef } from 'react'

type KeyPressHandler = (event: KeyboardEvent) => void

const useKeyPress = (
  key: string | string[],
  handler: KeyPressHandler,
  active?: boolean,
  options?: {
    triggerOnce?: boolean // If true, the handler will only be called once
    mustNotBeFocused?: boolean
    dependencies?: any[]
  }
) => {
  const lastKeyPressRef = useRef<number>(0)
  const lastSelectedRef = useRef<HTMLElement | null>(null)
  const { mustNotBeFocused = false, dependencies: dependancies = [] } =
    options || {}

  const isKey = useCallback((key: string | string[], eventKey: string) => {
    if (Array.isArray(key)) {
      return key.includes(eventKey)
    }
    return eventKey === key
  }, [])

  useEffect(() => {
    if (!active) return

    const handleKeyPress = (event: KeyboardEvent) => {
      let activeElement = document.activeElement as HTMLElement
      if (mustNotBeFocused) {
        if (activeElement) lastSelectedRef.current = activeElement
      }

      // If the key is Escape we check the previous active element since it will clear the focus
      if (event.key === 'Escape' && lastSelectedRef.current) {
        activeElement = lastSelectedRef.current
      }

      // Check for common interactive/focusable elements
      const isFocusableElement =
        activeElement &&
        (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(
          activeElement.tagName
        ) ||
          activeElement.isContentEditable)

      // Fire handler only if the element is not focusable or if mustNotBeFocused is false
      if (isKey(key, event.key) && (!mustNotBeFocused || !isFocusableElement)) {
        const now = Date.now()
        if (options?.triggerOnce && now - lastKeyPressRef.current < 300) return

        handler(event)
        lastKeyPressRef.current = now
      }
    }

    document.addEventListener('keydown', handleKeyPress, {
      capture: key === 'Escape' ? true : undefined,
    })

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [key, handler, active, mustNotBeFocused, ...dependancies])

  // Reset lastKeyPress when active changes
  useEffect(() => {
    if (active) {
      lastKeyPressRef.current = 0
    }
  }, [active])
}

export default useKeyPress
